import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import IosShareIcon from '@mui/icons-material/IosShare'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { FC, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../store'
import { AppActions, AppSelector } from '../../../store/app'
import { UserSelector } from '../../../store/User'
import { SnackbarActions } from '../../../store/snackbar'
import { WaterQualityIndexSelectors } from '../../../store/waterQualityIndex'
import { CloseIcon } from '../../icons/CloseIcon'
import { OutlinedButton } from '../../Button'
import { SippApi } from '../../../services/SippExpress'
import {
  generateWqiShareString,
  ShareGroup,
} from '../../../utils/shareStringGenerator'

const canUseWebShare = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function'

export const ShareResultsModal: FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const open = useSelector(AppSelector.getShareResultsModalState)
  const userAbbreviated = useSelector(UserSelector.makeUserAbbreviated)
  const overallStatus = useSelector(WaterQualityIndexSelectors.selectWaterSafetyStatus)
  const outcomeGroups = useSelector(WaterQualityIndexSelectors.selectOutcomeGroups)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [tokenLoading, setTokenLoading] = useState(false)

  const shareUrl = shareToken
    ? `${window.location.origin}/profile/overview?share=${shareToken}`
    : ''

  const shareGroups: ShareGroup[] = useMemo(
    () =>
      outcomeGroups.map((group) => ({
        groupKey: group.groupKey,
        status: WaterQualityIndexSelectors.mapApiStatusToUIStatus(group.status),
      })),
    [outcomeGroups],
  )

  const wordleString = useMemo(() => {
    if (!shareUrl) return ''
    return generateWqiShareString({
      overallStatus,
      groups: shareGroups,
      shareUrl,
    })
  }, [overallStatus, shareGroups, shareUrl])

  // Fetch share token when modal opens
  useEffect(() => {
    if (open) {
      setTokenLoading(true)
      SippApi.create
        .generateShareToken()
        .then((res: any) => {
          setShareToken(res.data.token)
        })
        .catch(() => {
          dispatch(
            SnackbarActions.showSnackbar({
              message: 'Failed to generate share link. Please try again.',
              options: { variant: 'error' },
            }),
          )
        })
        .finally(() => {
          setTokenLoading(false)
        })
    } else {
      setShareToken(null)
    }
  }, [open, dispatch])

  const handleClose = () => {
    dispatch(AppActions.toggleShareResultsModal())
    setRecipientEmail('')
    setMessage('')
    setEmailError('')
  }

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSendEmail = async () => {
    if (!recipientEmail) {
      setEmailError('Email is required')
      return
    }
    if (!validateEmail(recipientEmail)) {
      setEmailError('Please enter a valid email address')
      return
    }
    setEmailError('')
    setSending(true)
    try {
      await SippApi.create.shareResults({
        recipientEmail,
        senderName: userAbbreviated.full_name || 'A SIPP user',
        shareUrl,
        message: message || undefined,
      })
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Email sent successfully!',
          options: { variant: 'success' },
        }),
      )
      handleClose()
    } catch (error: any) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: error?.response?.data?.message || 'Failed to send email. Please try again.',
          options: { variant: 'error' },
        }),
      )
    } finally {
      setSending(false)
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Link copied to clipboard!',
          options: { variant: 'success' },
        }),
      )
    } catch {
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Failed to copy link',
        }),
      )
    }
  }

  const handleCopyWordle = async () => {
    try {
      await navigator.clipboard.writeText(wordleString)
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Snapshot copied — paste it anywhere.',
          options: { variant: 'success' },
        }),
      )
    } catch {
      dispatch(
        SnackbarActions.showSnackbar({
          message: 'Failed to copy snapshot',
          options: { variant: 'error' },
        }),
      )
    }
  }

  const handleNativeShare = async () => {
    if (!canUseWebShare()) return
    try {
      await navigator.share({
        title: 'My SIPP Water Report',
        text: wordleString,
      })
    } catch (err) {
      // User cancel or transient failure — no user-facing error needed.
      if ((err as Error)?.name !== 'AbortError') {
        dispatch(
          SnackbarActions.showSnackbar({
            message: 'Share dialog failed — try copy instead.',
          }),
        )
      }
    }
  }

  const actionsDisabled = tokenLoading || !shareToken
  const nativeShareAvailable = canUseWebShare()

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 6 },
        },
      }}
    >
      <DialogTitle>Share Your Results</DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent>
        {/* Wordle-style share snapshot */}
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          Share your water snapshot
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Copy the block and paste it into Slack, iMessage, or X — emoji included.
        </Typography>
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            p: 1.5,
            mb: 1.5,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            minHeight: 96,
          })}
        >
          {tokenLoading ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Preparing your share link…
              </Typography>
            </Stack>
          ) : wordleString ? (
            wordleString
          ) : (
            <Typography variant="caption" color="text.secondary">
              Unable to load your snapshot. Try reopening the share dialog.
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleCopyWordle}
            disabled={actionsDisabled || !wordleString}
            startIcon={<ContentCopyIcon />}
            fullWidth
          >
            Copy snapshot
          </Button>
          {nativeShareAvailable && (
            <OutlinedButton
              onClick={handleNativeShare}
              disabled={actionsDisabled || !wordleString}
              startIcon={<IosShareIcon />}
            >
              Share…
            </OutlinedButton>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Existing email + link flow */}
        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
          Or send it straight to someone
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Email the full report link, or just copy it to paste anywhere.
        </Typography>
        <TextField
          fullWidth
          label="Recipient Email"
          type="email"
          value={recipientEmail}
          onChange={(e) => {
            setRecipientEmail(e.target.value)
            if (emailError) setEmailError('')
          }}
          error={!!emailError}
          helperText={emailError}
          size="small"
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Personal Message (optional)"
          multiline
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          size="small"
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 1, mb: 1, px: 2 }}>
        <Button
          variant="contained"
          onClick={handleSendEmail}
          disabled={sending || actionsDisabled}
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ minWidth: 120 }}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
        <OutlinedButton onClick={handleCopyLink} disabled={actionsDisabled} startIcon={<ContentCopyIcon />}>
          Copy Link
        </OutlinedButton>
      </DialogActions>
    </Dialog>
  )
}
